import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import { CloseOutlined } from '@mui/icons-material';
import { LogoUpload } from '../../../components/common/LogoUpload';
import { Controller, useForm } from 'react-hook-form';
import { createCompanyAsync } from '../../../../application/features/company/companyThunk';
import { useAppDispatch } from '../../../../application/store/useStore';
import { toast } from 'react-toastify';
export const NewCompanyDialog = ({ onClose, openCompanyDialog }) => {
    const { t } = useTranslation();
    const [isUploading, setIsUploading] = React.useState(false);
    const [logoBase64, setLogoBase64] = React.useState(null);
    const uploadingIcon = isUploading ? _jsx(CircularProgress, { size: 12 }) : null;
    const dispatch = useAppDispatch();
    const requiredMessage = t('requiredField');
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {}
    });
    React.useEffect(() => {
        if (!openCompanyDialog) {
            reset({
                name: '',
                address: '',
                domain: '',
                phoneNumber: '',
                email: '',
                description: ''
            });
            setLogoBase64(null);
        }
    }, [reset, openCompanyDialog]);
    const resetForm = () => {
        reset({
            name: '',
            address: '',
            domain: '',
            phoneNumber: '',
            email: '',
            description: ''
        });
        setLogoBase64(null);
    };
    const createCompany = async (companyData) => {
        setIsUploading(true);
        // Add logo to company data if available
        const dataWithLogo = {
            ...companyData,
            logoBase64: logoBase64 || undefined
        };
        await dispatch(createCompanyAsync(dataWithLogo))
            .unwrap()
            .then((response) => {
            resetForm();
            onClose();
            toast.success(t('companyCreationSuccess'));
        })
            .catch((error) => {
            toast.error(t('companyCreationFail'));
        })
            .finally(() => {
            onClose();
            setIsUploading(false);
        });
    };
    return (_jsxs(Dialog, { maxWidth: 'sm', fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                overflow: 'hidden'
            }
        }, open: openCompanyDialog, onClose: onClose, children: [_jsxs(DialogTitle, { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1000,
                    padding: '16px'
                }, children: [t('createCompany'), _jsx(IconButton, { "aria-label": 'close', onClick: () => {
                            onClose();
                            resetForm();
                        }, sx: (theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.action.active
                        }), children: _jsx(CloseOutlined, {}) })] }), _jsx(DialogContent, { sx: {
                    overflowY: 'auto'
                }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '20px', children: [_jsx(LogoUpload, { value: logoBase64, onChange: setLogoBase64 }), _jsx(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'name', defaultValue: '', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        }
                                                    }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('name') })) }), errors.name && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.name.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'address', defaultValue: '', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('address') })) }), errors.address && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.address.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'domain', defaultValue: '', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('domain') })) }), errors.domain && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.domain.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'phoneNumber', defaultValue: '', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('phoneNumber') })) }), errors.phoneNumber && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.phoneNumber.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'email', defaultValue: '', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        }
                                                    }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('email') })) }), errors.email && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.email.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'description', defaultValue: '', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('description'), multiline: true, rows: 4 })) }), errors.description && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.description.message }))] }) })] }) })] }) }), _jsxs(DialogActions, { sx: {
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    zIndex: 1000,
                    padding: '16px'
                }, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', autoFocus: true, disabled: isUploading, onClick: () => {
                            onClose();
                            resetForm();
                        }, children: t('cancel') }), _jsx(Button, { type: 'submit', onClick: () => {
                            handleSubmit(createCompany)();
                        }, variant: 'contained', children: isUploading ? uploadingIcon : t('createCompany') })] })] }));
};
